import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Box, Container, Card, Grid, FormLabel, Stack, Switch, FormGroup, FormControlLabel, Toolbar, IconButton, Menu, Button } from '@mui/material';
import FilledChart from '../Charts/FilledChart';
import Api from '../../network/Api';
import RadarChart from '../Charts/RadarChart';
import HeatmapChart from '../Charts/HeatmapChart';
import ImpactChart from '../Charts/ImpactChart';
import GaugeChart from '../Charts/GaugeChart';
import dayjs from 'dayjs';
import { Category } from '../../types/Lessons';
import DatePicker, { DateRangeItem } from './DatePicker';
import { useCookies } from 'react-cookie';
import Placeholder from '../Shared/Placeholder';
import HistoryIcon from '@mui/icons-material/History';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import TimelineActions from '../Timeline/Actions';
import { ItemButtonActions } from "../Lessons/Buttons";
import { StartProps } from '../Layouts/MainApp';
import i18n from "i18next";

type DashboardValues = {
  categories: Category[];
  lessons: {
    scores: number[][];
    series: Series[];
    total: number;
  };
  perception_index: {
    score: number;
    valid: boolean;
  };
}

export type Series = {
  date: number;
  amount: number;
}

type DateRangeItems = {
  series: DateRangeItem;
  compare: DateRangeItem;
}

type StoredRanges = {
  offset: number;
  series: number; // stored range in days
  compare: number; // stored diff between endDate and series startDay in days
}

const Dashboard = (props: StartProps & ItemButtonActions) => {

  const [cookieRanges, setCookieRanges] = useCookies(['dateRanges']);
  const startDay = dayjs().add(cookieRanges.dateRanges?.offset ?? 0, "d")
  const seriesLength = cookieRanges.dateRanges?.series ?? 7
  const compareEnd = seriesLength + (cookieRanges.dateRanges?.compare ?? 1)

  const [annualTimeLost, setAnnualTimeLost] = useState<number>();
  const [annualCosts, setAnnualCosts] = useState<number>();
  const [values, setValues] = useState<DashboardValues>();
  const [compareValues, setCompareValues] = useState<DashboardValues>();
  const [severitiesField, setSeveritiesField] = useState<"perceptions"|"count">("perceptions");

  const [showsTimelineMenu, showTimelineMenu] = useState(false);
  const [timelineAnchorEl, setTimelineAnchorEl] = useState();

  const toggleTimelineMenu = (event) => {
    showTimelineMenu(!showsTimelineMenu);
    setTimelineAnchorEl(event.currentTarget);
  }

  const [dateRanges, setDateRanges] = useState<DateRangeItems>({
    series: {
      startDate: startDay.subtract(seriesLength, 'd').toDate(),
      endDate: startDay.toDate(),
      key: 'series'
    },
    compare: {
      startDate: startDay.subtract(compareEnd + seriesLength, 'd').toDate(),
      endDate: startDay.subtract(compareEnd, 'd').toDate(),
      key: 'compare'
    }
  });

  const loadValues = () => {
    Api.get(`/dashboard?from=${dayjs(dateRanges.series.startDate).unix()}&to=${dayjs(dateRanges.series.endDate).unix()}&compare_from=${dayjs(dateRanges.compare.startDate).unix()}&compare_to=${dayjs(dateRanges.compare.endDate).unix()}`).then(result => {
      setAnnualTimeLost(result.annual_time_lost);
      setAnnualCosts(result.annual_costs);
      setValues(result.series);
      setCompareValues(result.compare);
    });
  }

  useEffect(() => {
    const storedRanges: StoredRanges = { 
      offset: dayjs(dateRanges.series.endDate).diff(dayjs(), "d"),
      series: dayjs(dateRanges.series.endDate).diff(dayjs(dateRanges.series.startDate), "d"), 
      compare: dayjs(dateRanges.series.startDate).diff(dayjs(dateRanges.compare.endDate), "d") };
    setCookieRanges('dateRanges', storedRanges);
    loadValues();
  }, [dateRanges]);

  const changeRateLabel = (total: number, compareTotal?: number, inverse?: boolean) => {
    let changeRate = total / Math.max(compareTotal ?? 1, 1);
    let percent = changeRate * 100;
    return(changeRate != null && <FormLabel>
      <span className={percent >= 0 && inverse != true ? 'text-success' : 'text-error'} style={{fontFamily: 'var(--header-font)'}}>
        {percent >= 0 ? '+' : '-'}{Math.abs(percent).toFixed(1)}% change
      </span></FormLabel>);
  }

  return <Box sx={{overflowY: 'auto', flexGrow: 1, width: "100%", pt: { xs: 1, md: 1.5 }, pb: 5}}>
    <Container id="dashboard" maxWidth="xl">

      <Toolbar sx={{gap: 1, mb: { xs: 3, md: 0 }, flexDirection: {
          xs: 'column',
          sm: 'row'
        },
        alignItems: 'flex-start'}} 
        disableGutters>

        <Button size="small" startIcon={<PersonAddIcon />} variant="text" color="info" component={NavLink} to="#invite" sx={{display: { xs: 'none', md: 'inline-flex' }}}>Invite team</Button>
        <Button size="small" startIcon={<HistoryIcon />} variant="text" color="secondary" onClick={toggleTimelineMenu} sx={{display: { xs: 'none', md: 'inline-flex' }}}>Recent activity</Button>

        <Box sx={{ml: 'auto'}}></Box>

        <DatePicker label="Selected time range" dateRange={dateRanges.series} onChange={values => setDateRanges(current => { return {...current, ...values} })} />
        <DatePicker color="greyDark" label="Compared with" dateRange={dateRanges.compare} onChange={values => setDateRanges(current => { return {...current, ...values} })} />
      
        <Menu id="timeline-menu"
              anchorEl={timelineAnchorEl}
              open={showsTimelineMenu}
              onClose={toggleTimelineMenu}
              sx={{[`div.MuiPaper-root`]: { px: 2, py: 1 }}}>
          <TimelineActions {...props} />
        </Menu>

      </Toolbar>

      <Grid container spacing={3}>

        <Grid size={{xs: 12, sm: 6, md: 4}}>
          <Card>
            <h3>Lessons learned</h3>
            { values ? <>
              <Stack direction="row" alignItems="center" gap={2}>
                <h2 className='text-light'>{typeof(values.lessons?.total) !== "undefined" ? values.lessons.total : '-'} <FormLabel><span className="text-secondary">total</span></FormLabel></h2>
                { changeRateLabel(values.lessons?.total, compareValues?.lessons?.total) }
              </Stack>
              <div className='area-chart'>{<FilledChart series={values.lessons?.series} compare={compareValues?.lessons?.series ?? []} />}</div>
            </> : <Placeholder /> }
          </Card>
        </Grid>

        <Grid size={{xs: 12, sm: 6, md: 4}}>
          <Card>
            <h3>Annual time lost</h3>
            { values ? <>
              <Stack direction="row" alignItems="center" gap={2}>
                <h2 className='text-light'>{typeof(annualTimeLost) !== "undefined" ? `${Math.round(annualTimeLost / 60 * 100) / 100} h` : '-'}</h2>
                <FormLabel>
                  <span style={{fontFamily: 'var(--header-font)'}}>
                    past 12 months
                  </span>
                </FormLabel>
              </Stack>
            </> : <Placeholder /> }
            
            { typeof(annualCosts) !== "undefined" && <>
              <FormLabel sx={{mt: 1}}><span className="text-primary">Annual costs</span></FormLabel>

              <Stack direction="row" alignItems="center" gap={2}>
                <h2 className='text-light text-error'>{typeof(annualTimeLost) !== "undefined" ? `${i18n.t('currency_label', { val: annualCosts, maximumFractionDigits: 0 })}` : '-'}</h2>
                <FormLabel>
                  <span style={{fontFamily: 'var(--header-font)'}}>
                    {props.workspace.hourly_rate ? 'at ' + i18n.t('currency_label', { val: props.workspace.hourly_rate, maximumFractionDigits: 0 }) + ' / h' : 'No hourly rate set'}
                  </span>
                </FormLabel>
              </Stack>
            </> }
          </Card>
        </Grid>

        <Grid size={{xs: 12, sm: 6, md: 4}}>
          <Card>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <h3>Perception index</h3>
              { values && <FormLabel><span className={`text-${values.perception_index.valid === null ? "secondary" : values.perception_index.valid ? "success" : "error"}`} style={{fontFamily: 'var(--header-font)'}}>{values.perception_index.valid == null ? "Not enough data" : `${values.perception_index.valid ? "high" : "low"} validity`}</span></FormLabel> }
            </Stack>
            { values ? <>
              <div className='area-chart'><GaugeChart value={values.perception_index.score} /></div>
            </> : <Placeholder /> }
          </Card>
        </Grid>

        <Grid size={{xs: 12, sm: 6, md: 4}}>
          <Card>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <h3>Severities</h3>
              <FormGroup row sx={{pb:  "0 !important", alignItems: "center", gap: "0 !important"}}>
                <FormControlLabel
                  control={<Switch checked={severitiesField === 'perceptions'} onChange={(e, on) => setSeveritiesField(on ? "perceptions" : "count")} />}
                  label={<FormLabel>{severitiesField === 'perceptions' ? "Subjective" : "Objective"} score</FormLabel>}
                  labelPlacement="start" />
              </FormGroup>
            </Stack>
            { values ? <>
              <HeatmapChart categories={values.categories} field={severitiesField} />
            </> : <Placeholder /> }
          </Card>
        </Grid>

        <Grid size={{xs: 12, sm: 6, md: 4}}>
          <Card>
            <h3>Distributed risk score</h3>
            { values ? <>
             <ImpactChart series={values.lessons.scores} compare={compareValues?.lessons?.scores ?? []} />
            </> : <Placeholder /> }
          </Card>
        </Grid>

        <Grid size={{xs: 12, sm: 6, md: 4}}>
          <Card>
            <h3>Cause map</h3>
            { values ? <>
              <RadarChart series={values.categories} compare={compareValues?.categories ?? []} />
            </> : <Placeholder /> }
          </Card>
        </Grid>

      </Grid>

    </Container>
  </Box>;
};

export default Dashboard;